import Tool, { type ToolArgs } from '../../../tools/Tool';
import { isLinkCardElement } from '../../../types/guards';
import type { Placement } from '@floating-ui/dom';
import linkCardSize from '../../../assets/icons/linkCardSize.svg';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import { updateLinkCardProperty, hasImageOrPlaceholder } from '../utils';
import LinkCardCommon from '../LinkCardCommon';
import fullWidthSvg from '../../../assets/icons/fullWidth.svg';
import imageRatioSvg from '../../../assets/icons/imageRatio.svg';

class ToolLinkCardFullWidth extends Tool {
  static toolName = 'Full width';

  static renderMenuIcon(toolArgs: ToolArgs): JSX.Element {
    return svgToIcon(fullWidthSvg);
  }

  static action({ editor, element }: ToolArgs): void {
    if (!editor || !element || !isLinkCardElement(element)) return;

    const newValue = !element.linkCard_fullWidth;
    updateLinkCardProperty(editor, element, 'linkCard_fullWidth', newValue);

    // Store the fullWidth preference for future LinkCard insertions
    localStorage.setItem(
      LinkCardCommon.linkCardFullWidthPrefKey,
      String(newValue)
    );
  }

  static isActive({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return Boolean(element.linkCard_fullWidth);
  }
}

class ToolLinkCardKeepRatio extends Tool {
  static toolName = 'Keep image ratio';

  static renderMenuIcon(toolArgs: ToolArgs): JSX.Element {
    return svgToIcon(imageRatioSvg);
  }

  static action({ editor, element }: ToolArgs): void {
    if (!editor || !element || !isLinkCardElement(element)) return;
    const currentKeepRatio = element.linkCard_keepRatio;
    const newKeepRatio = !currentKeepRatio;
    updateLinkCardProperty(editor, element, 'linkCard_keepRatio', newKeepRatio);

    // Store the keepRatio preference for future LinkCard insertions
    localStorage.setItem(
      LinkCardCommon.linkCardKeepRatioPrefKey,
      String(newKeepRatio)
    );
  }

  static isActive({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return Boolean(element.linkCard_keepRatio);
  }

  static isEnabled({ element }: ToolArgs): boolean {
    if (!element || !isLinkCardElement(element)) return false;
    return hasImageOrPlaceholder(element.linkCard_image);
  }
}

class ToolLinkCardSize extends Tool {
  static toolName = 'Size';
  static withoutRefocus = true;

  static renderToolbarButtonIcon({ editor, element }: ToolArgs): JSX.Element {
    return svgToIcon(linkCardSize);
  }

  static submenuTools = () => [[ToolLinkCardFullWidth, ToolLinkCardKeepRatio]];

  static submenuType = 'list-toggles' as const;

  static tooltipPlacement(): Placement {
    return 'bottom';
  }

  static isEnabled({ element }: ToolArgs): boolean {
    return Boolean(element && isLinkCardElement(element));
  }
}

export default ToolLinkCardSize;
